import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

/**
 * LinkOverlay — the branded CTA banner shown before a short link forwards to its
 * destination. Rendered by the Redirector when a link carries an `overlay`
 * config. Mirrors the server's render_overlay_html, but as a native page so the
 * SPA redirect path shows it too. Never frames the destination.
 *
 * Props:
 *  - overlay: { theme, headline?, message?, button_text?, button_url?, countdown? }
 *  - destination: the final URL to continue to
 *  - onContinue: () => void  (perform the actual redirect)
 */

const THEMES = {
  candy: { page: "bg-paper text-ink", accent: "bg-candy text-white", border: "border-ink" },
  mint: { page: "bg-paper text-ink", accent: "bg-mint text-white", border: "border-ink" },
  yellow: { page: "bg-paper text-ink", accent: "bg-yellow text-ink", border: "border-ink" },
  ink: { page: "bg-ink text-paper", accent: "bg-candy text-white", border: "border-paper" },
};

export default function LinkOverlay({ overlay, destination, onContinue }) {
  const t = THEMES[overlay?.theme] || THEMES.candy;
  const initial = Number.isFinite(overlay?.countdown) ? overlay.countdown : null;
  const [left, setLeft] = useState(initial && initial > 0 ? initial : null);

  useEffect(() => {
    if (left == null) return;
    if (left <= 0) {
      onContinue();
      return;
    }
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left, onContinue]);

  return (
    <div
      className={`flex min-h-screen items-center justify-center p-6 ${t.page}`}
      data-testid="link-overlay"
    >
      <main
        className={`w-full max-w-[480px] rounded-[20px] border-[2.5px] ${t.border} p-9 text-center shadow-sticker-md`}
      >
        <span
          className={`inline-block rounded-full border-2 ${t.border} px-3 py-1 font-mono text-[11px] font-extrabold uppercase tracking-[0.18em] ${t.accent}`}
        >
          Sponsored message
        </span>

        {overlay?.headline ? (
          <h1 className="mt-5 font-display text-2xl font-extrabold leading-tight tracking-tight">
            {overlay.headline}
          </h1>
        ) : null}

        {overlay?.message ? (
          <p className="mt-3 font-sans text-[15px] leading-relaxed opacity-80">{overlay.message}</p>
        ) : null}

        {overlay?.button_text && overlay?.button_url ? (
          <a
            href={overlay.button_url}
            target="_blank"
            rel="noopener nofollow"
            data-testid="overlay-cta"
            className={`mt-6 inline-flex items-center gap-2 rounded-full border-[2.5px] ${t.border} px-7 py-3 font-display text-[15px] font-extrabold shadow-sticker-xs ${t.accent}`}
          >
            {overlay.button_text}
            <ArrowRight size={16} strokeWidth={3} />
          </a>
        ) : null}

        <button
          type="button"
          onClick={onContinue}
          data-testid="overlay-continue"
          className="mt-5 block w-full font-sans text-sm font-bold underline decoration-2 underline-offset-4 opacity-80 transition-opacity hover:opacity-100"
        >
          Continue to site →
        </button>

        {left != null ? (
          <p className="mt-4 font-mono text-xs opacity-60" data-testid="overlay-countdown-live">
            Continuing in {left}s…
          </p>
        ) : null}

        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] opacity-45">
          via LinkLane
        </p>
      </main>
    </div>
  );
}
